import { Component, Input, OnInit, Output, EventEmitter, CUSTOM_ELEMENTS_SCHEMA, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, AbstractControl } from '@angular/forms'; 
import { CommonModule } from '@angular/common';
import { DonationCardService } from '../../../services/donation-card.service';
import { SelectListItem, LetterCmmVm, DntComplianceLetterType, ResponseActions, DonationCardGeneralInfoVM, DonationComplianceLetterViewModel, FECComplianceUpdateViewModel, AttributionModel, DonationComplianceAttributionModel, AttributedDonorTypes } from '../../../models/DonationCardModels';
import { GenericButtonComponent } from '../../../../global/component/generic/generic-button/generic-button.component';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { CardModule } from 'primeng/card';
import { DropdownModule } from 'primeng/dropdown';
import { CalendarModule } from 'primeng/calendar';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast'; 
import { AutoCompleteModule } from 'primeng/autocomplete';
import { catchError, debounceTime, distinctUntilChanged, Subscription } from 'rxjs';
import { DonorSearchComponent } from '../../donor-search/donor-search.component';
import { SearchService } from '../../../../global/services/data/search-service/searchservice';
import { DonorFundTotalsGridComponent } from '../../../shared-components/donor-fund-totals-grid/donor-fund-totals-grid.component';
import { CheckboxModule } from 'primeng/checkbox';
import { restrictToTwoDecimals } from '../../../../global/util/util';

@Component({
  selector: 'app-donation-letters-tab',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule, 
    GenericButtonComponent,
    TableModule,
    DialogModule,
    CardModule,
    DropdownModule,
    CalendarModule,
    ButtonModule,
    InputTextModule,
    InputTextareaModule,
    ToastModule,
    AutoCompleteModule,
    DonorSearchComponent,
    DonorFundTotalsGridComponent,
    GenericButtonComponent,
    CheckboxModule
  ],
  templateUrl: './donation-letters-tab.component.html',
  styleUrl: './donation-letters-tab.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [MessageService] 
})
export class DonationLettersTabComponent implements OnInit, OnDestroy {
  @Input() donationId!: number;
  @Input() splitId!: number;
  @Input() campaignId!: number;
  @Input() generalInfo?: DonationCardGeneralInfoVM; 
  @Input() fecInfo?: FECComplianceUpdateViewModel;
  @Input() isReadOnlyMode: boolean = false;

  @Output() addLetter = new EventEmitter<void>();
  @Output() refreshTransactions = new EventEmitter<void>();
  @Output() scrollToTransactionHistory = new EventEmitter<void>(); // Add new event emitter

  donationLetterForm!: FormGroup;
  attributionTransactionForm!: FormGroup;
  donationComplianceVM!: DonationComplianceLetterViewModel;
  letters: LetterCmmVm[] = [];
  donationLetterDetails?: LetterCmmVm;
  showDonationLetterDialog: boolean = false;
  letterTypes: SelectListItem[] = [];
  allLetterTypes: SelectListItem[] = [];
  fecLineNumbers: SelectListItem[] = [];
  responseActions: SelectListItem[] = [];
  showRefundAmount: boolean = false;
  showPermissibleFields: boolean = false;
  showAttributionTransactionFields: boolean = false;
  errorMessage: string = '';
  disableSaveButton: boolean = false;

  showDeleteDialog: boolean = false; 
  letterTypeIdToDelete!: number; 
  maxRefundRemaining: number = 0; 

  showAttributionTransactionModal: boolean = false;
  parentDonationAmount: number = 0;
  pendingAttributedDonors: any[] = [];
  attributedDonors: any[] = [];
  pendingAttributedDonorsTotal: number = 0;
  attributedDonorsTotal: number = 0;

  fundCodeSuggestions: any[] = [];
  selectedDonor: any = null;
  showDonorSearchDialog: boolean = false;
  private subscriptions: Subscription = new Subscription();

  subscription: Subscription = new Subscription();

  constructor(
    private service: DonationCardService,
    private fb: FormBuilder,
    private messageService: MessageService,
    private searchService: SearchService
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.loadLetterTypes();
    this.calculateRemainingAmount();
    this.subscriptions.add(
      this.searchService.donorSelected$.subscribe((donor: any) => {
        if (donor) {
          this.selectedDonor = {
            donorId: donor.DonorID,
            firstName: donor.FirstName,
            lastName: donor.LastName
          };
          this.showDonorSearchDialog = false;
        }
      })
    );

    this.subscription.add(
      this.searchService.saveDonor$.subscribe((donor) => {
        if (donor) {
          this.selectedDonor = {
            donorId: donor?.donorId,
            firstName: donor?.firstName,
            lastName: donor?.lastName
          };
        }
      })
    );
  }

  onResponseActionChange(responseActionId: number): void {

    if (
      Number(responseActionId) === Number(ResponseActions.AttributePartner) ||
      Number(responseActionId) === Number(ResponseActions.AttributeSoleProprietor)
    ) {
      const fecLineNumber = this.fecInfo?.fecLineNumberId;
      this.donationLetterForm.get('responseActionId')?.setValue(responseActionId);
      this.donationLetterForm.get('fecLineNumberId')?.reset();
      this.donationLetterForm.get('refundAmount')?.reset();

      if (!fecLineNumber) {
        this.errorMessage = 'Please add FEC Line Number for parent donation to attribute!';
        this.disableSaveButton = true;
        return;
      } else if (!this.isAttributionAllowed()) {
        this.errorMessage = `Attribution not allowed for donor type: ${this.generalInfo?.donorTypeName}`;
        this.disableSaveButton = true;
        return;
      } else if (!this.donationLetterForm.valid) {
        this.donationLetterForm.markAllAsTouched();
        this.messageService.add({ severity: 'error', summary: 'Validation Error', detail: 'Please fix validation errors in the form!' });
      } else {
        this.errorMessage = '';
        this.disableSaveButton = false;
        this.showAttributionTransactionFields = true;
        this.selectedDonor = null;

        // Initialize the attributionTransactionForm only when the fields are shown
        this.attributionTransactionForm = this.fb.group({
          fundCodeId: [null, Validators.required],
          fundCode: [null, Validators.required],
          amount: [null, Validators.required],
          adjustmentDate: [null, Validators.required],
          fecLineNumberId: [null, Validators.required],
          fecDescription: [''],
          customNote: [''],
          refundExcess: [false], // New checkbox field
          refundAmount: [{ value: null, disabled: true }, [Validators.required, this.validateRefundAmount.bind(this)]], // New refund amount field
        });

        this.loadAttributionData();
      }
    } else {
      this.errorMessage = '';
      this.disableSaveButton = false;
      this.showAttributionTransactionFields = false;
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  loadLetterTypes(): void {
    this.service.getLetters(this.donationId, this.campaignId).subscribe(data => {
      this.donationComplianceVM = data || {};
      this.letters = data.selectedLetterCmmVm || [];
      const existingLetterTypeIds = this.letters.map(letter => letter.letterTypeId);
      this.allLetterTypes = data.letterTypes.map(item => ({ value: item.value, text: item.text }));
      this.letterTypes = data.letterTypes
        .filter(item => !existingLetterTypeIds.includes(Number(item.value))) 
        .map(item => ({ value: item.value, text: item.text }));
      this.fecLineNumbers = data.fecLineNumbers.map(item => ({ value: item.value, text: item.text }));
    });
  }

  initializeForm(): void {
    this.donationLetterForm = this.fb.group({
      letterTypeId: [null, Validators.required],
      sendDate: [null, Validators.required],
      responseDate: [null, Validators.required],
      responseActionId: [null, Validators.required],
      fecLineNumberId: [null],
      fecDescription: [''],
      refundedAmount: [0],
      refundAmount: [null, [Validators.required, this.validateRefundAmount.bind(this)]], 
      attributionAllowed: [false]
    });

    this.resetFields();

    this.donationLetterForm.get('responseActionId')?.valueChanges.subscribe(responseAction => {
      const letterType = this.donationLetterForm.get('letterTypeId')?.value;
      this.resetFields();

      if ((responseAction === ResponseActions.Refund || responseAction === ResponseActions.RefundNoResponse) &&
          [DntComplianceLetterType.ForeignDonor, DntComplianceLetterType.NonFedVerification, DntComplianceLetterType.OverLimit,
           DntComplianceLetterType.Redesignation, DntComplianceLetterType.PACMultiRedesignation, DntComplianceLetterType.PACNonRedesignation,
           DntComplianceLetterType.PresumptiveReattribution, DntComplianceLetterType.DebtRetirement, DntComplianceLetterType.OverLimitwithDebt,
           DntComplianceLetterType.BusinessVerification, DntComplianceLetterType.PartnershipVerification, DntComplianceLetterType.LLCVerificiation,
           DntComplianceLetterType.Reattribution].includes(letterType)) {
        this.showRefundAmount = true;
        this.donationLetterForm.get('refundAmount')?.setValidators([Validators.required, this.validateRefundAmount.bind(this)]);
        this.donationLetterForm.get('refundAmount')?.updateValueAndValidity();
        this.showPermissibleFields = false;
        this.showAttributionTransactionFields = false;
        this.calculateRemainingAmount(); 
      } else if (responseAction === ResponseActions.Permissible &&
                 [DntComplianceLetterType.ForeignDonor, DntComplianceLetterType.NonFedVerification].includes(letterType)) {
        if (letterType === DntComplianceLetterType.NonFedVerification && this.isAddMode()) {
          this.donationLetterForm.get('fecDescription')?.setValue('PERMISSIBLE FUNDS');
          if (this.fecInfo?.fecLineNumberId) {
            this.donationLetterForm.get('fecLineNumberId')?.setValue(this.fecInfo.fecLineNumberId.toString());
          }
        }
        if (letterType === DntComplianceLetterType.ForeignDonor && this.isAddMode()) {
          this.donationLetterForm.get('fecDescription')?.setValue(this.fecInfo?.fecDescription);
          if (this.fecInfo?.fecLineNumberId) {
            this.donationLetterForm.get('fecLineNumberId')?.setValue(this.fecInfo.fecLineNumberId.toString());
          }
        }
        this.showRefundAmount = false;
        this.showPermissibleFields = true;
        this.donationLetterForm.get('fecLineNumberId')?.setValidators(Validators.required);
        this.donationLetterForm.get('fecDescription')?.setValidators(Validators.required);
        this.donationLetterForm.get('fecLineNumberId')?.updateValueAndValidity();
        this.donationLetterForm.get('fecDescription')?.updateValueAndValidity();
        this.showAttributionTransactionFields = false;
      } else if ((responseAction === ResponseActions.AttributePartner || responseAction === ResponseActions.AttributeSoleProprietor) &&
                 [DntComplianceLetterType.BusinessVerification, DntComplianceLetterType.PartnershipVerification, DntComplianceLetterType.LLCVerificiation].includes(letterType)) {
        const fecLineNumber = this.donationLetterForm.get('fecLineNumberId')?.value;
        if (!fecLineNumber) {
          this.errorMessage = 'Please add FEC Line Number for parent donation to attribute!';
          this.disableSaveButton = true;
          this.showAttributionTransactionFields = false;
          this.showRefundAmount = false;
          this.showPermissibleFields = false;
        } else {
          this.errorMessage = '';
          this.disableSaveButton = false;
          this.donationLetterForm.get('attributionAllowed')?.setValue(true);
          this.showAttributionTransactionFields = true;
          this.errorMessage = '';
          this.disableSaveButton = false;
          this.showRefundAmount = false;
          this.showPermissibleFields = false;
          this.showAttributionForm();
        }
      } else {
        this.showRefundAmount = false;
        this.showPermissibleFields = false;
        this.showAttributionTransactionFields = false;
        this.donationLetterForm.get('refundAmount')?.clearValidators();
        this.donationLetterForm.get('refundAmount')?.updateValueAndValidity();
        this.donationLetterForm.get('fecLineNumberId')?.clearValidators();
        this.donationLetterForm.get('fecDescription')?.clearValidators();
        this.donationLetterForm.get('fecLineNumberId')?.updateValueAndValidity();
        this.donationLetterForm.get('fecDescription')?.updateValueAndValidity();
      }
    });
  }

  showAttributionForm(): void {
    this.selectedDonor = null;
    this.attributionTransactionForm = this.fb.group({
      fundCodeId: [null, Validators.required],
      fundCode: [null, Validators.required],
      amount: [null, Validators.required],
      adjustmentDate: [null, Validators.required],
      fecLineNumberId: [null, Validators.required],
      fecDescription: [''],
      customNote: ['']
    });
    this.loadAttributionData();
    this.showAttributionTransactionFields = true;
  }

  onRefundCheckboxChange(): void {
    const refundExcess = this.attributionTransactionForm.get('refundExcess')?.value;
    const refundAmountControl = this.attributionTransactionForm.get('refundAmount');
    if (refundExcess) {
      refundAmountControl?.enable();
    } else {
      refundAmountControl?.disable();
      refundAmountControl?.reset();
    }
  }

  removeSelectedDonor(): void {
    this.selectedDonor = null;
  }

  isAddMode(): boolean {
    return this.showDonationLetterDialog && !this.donationLetterDetails;
  }

  calculateRemainingAmount(): void {
    this.service.getRefundAmount(this.splitId).subscribe(data => {
      const donationAmount = this.generalInfo?.donationAmount || 0;
      const refundAmount = data.refundAmount ?? 0;
      this.maxRefundRemaining = donationAmount - refundAmount;
    });

  }

  validateRefundAmount(control: AbstractControl): { [key: string]: any } | null {
    const refundExcess = this.attributionTransactionForm?.get('refundExcess')?.value;
    const value = control.value;

    if (!refundExcess) {
      return null;
    }

    if (!value) {
      return { required: true };
    }

    return null;
  }

  resetFields(): void {
    this.donationLetterForm.get('attributionAllowed')?.setValue(false);

    this.showRefundAmount = false;
    this.showPermissibleFields = false;
    this.showAttributionTransactionFields = false;

    this.errorMessage = '';
    this.disableSaveButton = false;
  }

  onAddLetterClick(): void {
    this.showDonationLetterDialog = true;
    this.donationLetterForm.reset(); 
    this.showRefundAmount = false;
    this.showPermissibleFields = false;
    this.showAttributionTransactionFields = false;

    // Re-filter letter types to exclude existing ones
    const existingLetterTypeIds = this.letters.map(letter => letter.letterTypeId);
    this.letterTypes = this.letterTypes.filter(item => !existingLetterTypeIds.includes(Number(item.value)));

    // Enable fields explicitly
    this.donationLetterForm.get('letterTypeId')?.enable();
    this.donationLetterForm.get('sendDate')?.enable();
    this.donationLetterForm.get('responseDate')?.enable();
    this.donationLetterForm.get('responseActionId')?.enable();
  }

  onEditLetterClick(letterTypeId: number): void {
    this.service.getDonationLetterTypeDetails(this.donationId, this.splitId, letterTypeId, this.campaignId).subscribe({
      next: (data) => {
        // Include the letter type being edited in the dropdown
        this.donationLetterDetails = data;
        const existingLetterTypeIds = this.letters.map(letter => letter.letterTypeId);
        this.letterTypes = this.allLetterTypes.filter(item => !existingLetterTypeIds.includes(Number(item.value)) || Number(item.value) === letterTypeId);

        this.responseActions = data.returnLetterTypes?.map((item: SelectListItem) => ({
          value: item.value,
          text: item.text
        })) || [];
        this.fecLineNumbers = data.fecLineNumbers?.map(item => ({ value: item.value, text: item.text })) || [];
        this.donationLetterForm.patchValue({
          letterTypeId: this.letterTypes.find(option => Number(option.value) === data.letterTypeId)?.value || null,
          sendDate: data.dateSent ? new Date(data.dateSent) : null, // Convert to Date object
          responseDate: data.responseReceivedDate ? new Date(data.responseReceivedDate) : null, // Convert to Date object
          fecLineNumberId: data.fecLineNumberId ? this.fecLineNumbers.find(option => Number(option.value) === data.fecLineNumberId)?.value : null,
          fecDescription: data.fecDescription,
          refundAmount: data.complianceLetterTypeRefundAmount,
          responseActionId: this.responseActions.find(option => Number(option.value) === data.returnLetterTypeId)?.value || null,
        });
        this.attributedDonors = data.attributedDonors || [];

       this.calculateRemainingAmount();

        // Disable specified fields
        this.donationLetterForm.get('letterTypeId')?.disable();
        this.donationLetterForm.get('sendDate')?.disable();
        this.donationLetterForm.get('responseDate')?.disable();
        this.donationLetterForm.get('responseActionId')?.disable();

        this.showDonationLetterDialog = true;
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load donation letter details. Please try again.' });
      }
    });
  }

  saveDonationLetter(refundAmount: number = 0, savingLetterAfterAttribution: boolean = false): void {
    if (this.isReadOnlyMode) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Read Only',
        detail: 'This tab is in read-only mode. Saving is disabled.'
      });
      return;
    }

    if (this.donationLetterForm.valid) {
      const letterData = {
        letterTypeId: this.donationLetterForm.get('letterTypeId')?.value, 
        dateSent: this.donationLetterForm.get('sendDate')?.value,
        responseReceivedDate: this.donationLetterForm.get('responseDate')?.value,
        returnLetterTypeId: this.donationLetterForm.get('responseActionId')?.value, 
        fECLineNumberId: this.donationLetterForm.get('fecLineNumberId')?.value, 
        fecDescription: this.donationLetterForm.get('fecDescription')?.value,
        refundAmount: savingLetterAfterAttribution ? refundAmount : this.donationLetterForm.get('refundAmount')?.value,
        attributionAllowed: this.donationLetterForm.get('attributionAllowed')?.value,
        donationAmount: this.generalInfo?.donationAmount || 0, 
      };

      if ((this.donationLetterForm.get('refundAmount')?.value &&
        this.donationLetterForm.get('refundAmount')?.value > 0 &&
        this.maxRefundRemaining < this.donationLetterForm.get('refundAmount')?.value) && !savingLetterAfterAttribution) {
          this.messageService.add({
            severity: 'error',
            summary: 'Validation Error',
            detail: 'The refund amount should be less than or equal to the remaining refund amount.'
          });
          return;
        }

      this.service.saveLetter(letterData, this.donationId, this.splitId).subscribe({
        next: () => {
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Donation letter saved successfully.' });
          this.showDonationLetterDialog = false;

          const responseActionId = this.donationLetterForm.get('responseActionId')?.value;
          if (responseActionId === ResponseActions.Reattribution ||
            responseActionId === ResponseActions.Redesignation) {
            this.messageService.add({
              severity: 'info',
              summary: `${responseActionId === ResponseActions.Reattribution ? 'Reattribution' : 'Redesignation'} Required`,
              detail: `Please ${responseActionId === ResponseActions.Reattribution ? 'Reattribute' : 'Redesignate'} Donation Using Compliance Action Panel.`
            });

            this.scrollToTransactionHistory.emit();
          }

          if ((refundAmount && refundAmount > 0) ||
              (letterData.refundAmount && letterData.refundAmount > 0)) {
            window.location.reload();  
          } else {
            this.loadLetterTypes(); 
          }
        },
        error: () => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save donation letter. Please try again.' });
        }
      });
    } else {
      if (this.donationLetterForm.get('refundAmount')?.hasError('refundExceedsRemaining')) {
        this.messageService.add({ severity: 'error', summary: 'Validation Error', detail: 'Refund amount exceeds remaining amount.' });
      } else {
        this.messageService.add({ severity: 'error', summary: 'Validation Error', detail: 'Please fill all required fields before saving.' });
      }
    }
  }

  onDeleteLetter(letterTypeId: number): void {
    this.letterTypeIdToDelete = letterTypeId; 
    this.showDeleteDialog = true;
  }

  confirmDeleteLetter(): void {
    const letterData = {
      DonationComplLetterTypeId: this.letterTypeIdToDelete,
      DonationId: this.donationId,
      SplitId: this.splitId
    };

    this.service.deleteLetter(letterData).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Donation compliance letter deleted successfully.' });
        window.location.reload();  
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete donation compliance letter. Please try again.' });
        this.closeDeleteDialog(); 
      }
    });
  }

  closeDeleteDialog(): void {
    this.showDeleteDialog = false; 
  }

  onLetterTypeChange(letterTypeId: number): void {
    this.service.getResponseActions(letterTypeId).subscribe(data => {
      this.responseActions = data.map((item: SelectListItem) => ({
        value: item.value,
        text: item.text
      }));
      this.donationLetterForm.get('responseActionId')?.reset();
      this.showRefundAmount = false;
      this.showPermissibleFields = false;
      this.showAttributionTransactionFields = false;
    });
  }

  private isAttributionAllowed(): boolean {
      const donorTypeId = this.generalInfo?.donorTypeId;

    if (donorTypeId === Number(AttributedDonorTypes.LLC) ||
        donorTypeId === Number(AttributedDonorTypes.LLP) ||
        donorTypeId === Number(AttributedDonorTypes.ORG)
      ) {
        return true;
      }

      return false;
  }

  loadAttributionData(): void {
    this.parentDonationAmount = this.generalInfo?.donationAmount ?? 0;

    // Set fecDescription based on response action
    const selectedResponseAction = this.donationLetterForm.get('responseActionId')?.value;
    const parentDonorName = this.generalInfo?.donorName || '';

    if (selectedResponseAction === ResponseActions.AttributeSoleProprietor) {
      this.attributionTransactionForm.get('fecDescription')?.setValue(`${parentDonorName}: SOLE PROPRIETOR`);
    } else if (selectedResponseAction === ResponseActions.AttributePartner) {
      this.attributionTransactionForm.get('fecDescription')?.setValue(`${parentDonorName}: PARTNER`);
    } else {
      this.attributionTransactionForm.get('fecDescription')?.setValue(`${parentDonorName}: PERMISSIBLE FUNDS`);
    }

    this.calculateDonorTotals();
  }

  calculateDonorTotals(): void {
    this.pendingAttributedDonorsTotal = this.pendingAttributedDonors.reduce((sum, donor) => sum + donor.amount, 0);
    this.attributedDonorsTotal = this.attributedDonors.reduce((sum, donor) => sum + donor.amount, 0);
  }

  saveAttributionTransaction(): void {
    if (this.isReadOnlyMode) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Read Only',
        detail: 'This tab is in read-only mode. Saving is disabled.'
      });
      return;
    }

    const refundAmount = this.attributionTransactionForm.get('refundAmount')?.value;

    if (refundAmount > this.maxRefundRemaining) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'The refund amount exceeds the maximum allowed refund amount.',
      });
      return;
    }

    // Existing logic for saving attribution transaction
    if (this.pendingAttributedDonors.length > 0) {
      const attributedDnrList: AttributionModel[] = this.pendingAttributedDonors.map(donor => ({
        DonorId: donor.donorId,
        FecLineNumberId: donor.fecLineNumberId,
        FundCodeId: donor.fundCodeId,
        Amount: donor.amount,
        FecDesc: donor.fecDescription,
        AdjustmentDate: donor.adjustmentDate,
        Notes: donor.customNote
      }));

      const refundAmount = this.attributionTransactionForm.get('refundExcess')?.value
        ? this.attributionTransactionForm.get('refundAmount')?.value
        : 0;

      const payload: DonationComplianceAttributionModel = {
        DonationId: this.donationId,
        SplitId: this.splitId,
        RefundAmount: refundAmount,
        SelectedDonationRespActionTypeId: this.donationLetterForm.get('responseActionId')?.value,
        AttributedDnrList: attributedDnrList
      };

      this.service.applyDonationComplianceAttribution(payload).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Attribution transaction saved successfully.'
          });
          this.refreshTransactions.emit();
          this.refreshPendingDonorsGrid();
          this.pendingAttributedDonors = [];
          this.attributionTransactionForm.reset();
          this.selectedDonor = null;
          this.showDonationLetterDialog = false;
          this.loadLetterTypes();
          this.saveDonationLetter(refundAmount, true);
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to save attribution transaction. Please try again.'
          });
        }
      });
    } else {
      this.attributionTransactionForm.markAllAsTouched();
      if (this.pendingAttributedDonors.length === 0) {
        this.messageService.add({
          severity: 'error',
          summary: 'Validation Error',
          detail: 'Please add at least one attribution before saving.'
        });
      }
    }
  }

  private mapToLabelValue(items: any[]): { label: string, value: string }[] {
    return items?.map((item: any) => ({ label: item.text, value: item.value })) || [];
  }

  onFundCodeSearch(event: any): void {
    const searchInput = event.query;
    const splitId = this.splitId; 
    const campaignId = this.campaignId;

    this.service.searchFundCodesByDonationId(searchInput, splitId, campaignId).pipe(
      debounceTime(300),
      distinctUntilChanged(),
      catchError(() => [])
    ).subscribe((data: any) => {
      this.fundCodeSuggestions = this.mapToLabelValue(data);
    });
  }

  onFundCodeSelect(event: any): void {
    this.attributionTransactionForm.get('fundcode')?.setValue(event?.value?.label);
    this.attributionTransactionForm.get('fundCodeId')?.setValue(event?.value?.value);
  }

  onSearchDonorClick(): void {
    this.showDonorSearchDialog = true;
  }

  onDonorSelected(donor: any): void {
    this.selectedDonor = donor;
    this.showDonorSearchDialog = false;
  }

  hideDonorSearchDialog(): void {
    this.showDonorSearchDialog = false;
  }

  onContinue(): void {
    if (this.attributionTransactionForm.invalid) {
      this.attributionTransactionForm.markAllAsTouched();
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fill all required fields before continuing.'
      });
      return; 
    }

    if (!this.selectedDonor) {
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please select donor to continue.'
      });
      return;
    }

    const formData = this.attributionTransactionForm.value;
    this.pendingAttributedDonors.push({
      ...formData,
      donorId: this.selectedDonor.donorId,
      name: `${this.selectedDonor.firstName} ${this.selectedDonor.lastName}`
    });

    this.calculateDonorTotals(); 
    this.attributionTransactionForm.reset({
      fundCodeId: null,
      fundCode: null,
      amount: null,
      adjustmentDate: null,
      fecLineNumberId: null,
      fecDescription: '',
      customNote: '',
      refundExcess: this.attributionTransactionForm.get('refundExcess')?.value, 
      refundAmount: this.attributionTransactionForm.get('refundAmount')?.value 
    }, { emitEvent: false });

    Object.keys(this.attributionTransactionForm.controls).forEach(key => {
      this.attributionTransactionForm.get(key)?.setErrors(null);
    });
    this.selectedDonor = null; 
  }

  refreshPendingDonorsGrid(): void {
    // Logic to refresh the pending attributed donors grid
  }

  closeDonorSearch(): void {
    this.showDonorSearchDialog = false;
  }

  onAmountInput(event: Event, controlName: string, formName: string = ''): void {
    const input = event.target as HTMLInputElement;
    const formattedValue = restrictToTwoDecimals(input.value);
    input.value = formattedValue;
    if(formName === 'attributionTransactionForm') {
      this.attributionTransactionForm.get(controlName)?.setValue(formattedValue, { emitEvent: false });
      return;
    }
    this.donationLetterForm.get(controlName)?.setValue(formattedValue, { emitEvent: false });
  }
}
